import { Autocomplete, AutocompleteChangeDetails, Button, Grid, TextField, Tooltip } from '@mui/material';
import CustomAccordion from '../CustomAccordion/CustomAccordion';
import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import _ from 'lodash';
import { resetAgentInput, setAgentInputExpand, setSelectedAgentId, setTriggerFocusCompanyNameInput, setAgentInput, setCountry, setAgentName, setContactDetails, setPorts, setRemark, setCargoesHandling } from './reducers/AgentInputReducer';
import BaseResponse from '../../app/BaseResponse';
import Utility from '../../app/utility';
import { showLoading } from '../LoadingView/reducers/LoadingViewReducer';
import { agentInputSelector } from './reducers/AgentInputReducer';
import AgentApi from './api/AgentApi';
import AgentModel from './models/AgentModel';
import { contactInputSelector } from '../Contact/reducers/ContactInputReducer';
import { CountryModel } from '../Contact/models/ContactStaticResourcesModel';
import PortMultipleSelector from '../PortSelector/PortMultipleSelector';
import PortModel from '../PortSelector/models/PortModel';
import { portSelectorSelector } from '../PortSelector/reducers/PortSelectorReducer';
import CargoTypeMultipleSelector from '../CargoTypeSelector/CargoTypeMultipleSelector';
import CargoTypeModel from '../CargoTypeSelector/models/CargoTypeModel';

const AgentInput: React.FunctionComponent = () => {
    const dispatch = useDispatch();
    const agentInputViewData = useSelector(agentInputSelector);
    const contactInputViewData = useSelector(contactInputSelector);
    let portSelectorData = useSelector(portSelectorSelector);

    useEffect(() => {
        if (agentInputViewData.triggerSave > 0) {
            save();
        }
    }, [agentInputViewData.triggerSave]);

    useEffect(() => {
        if (agentInputViewData.triggerRefreshInput > 0) {
            refreshInput();
        }
    }, [agentInputViewData.triggerRefreshInput]);

    const refreshInput = () => {
        dispatch(resetAgentInput());
        dispatch(setSelectedAgentId(""));
        dispatch(setTriggerFocusCompanyNameInput((new Date()).getTime()));
    }

    useEffect(() => {
        if (agentInputViewData.selectedAgentId.length > 0) {
            selectAgentById(agentInputViewData.selectedAgentId);
        }
    }, [agentInputViewData.selectedAgentId])

    const selectAgentById = (id: string) => {
        dispatch(showLoading(true));
        AgentApi.GetById({
            id: id
        }).then((response: BaseResponse<AgentModel>) => {
            if (response.success) {
                dispatch(setAgentInput(response.data));

                dispatch(showLoading(false));

            } else {
                Utility.Alert.Error(response.message);
                dispatch(showLoading(false));
            }
        }).catch((error) => {
            Utility.Alert.Error(error);
            dispatch(showLoading(false));
        })
    };

    const portsToIds = (ports: PortModel[]) => {
        return _.map(ports, (port) => {
            return port.id
        })
    }

    const getPortsByIds = (portIds: number[]) => {
        return _.filter(portSelectorData.ports, (port) => {
            return _.indexOf(portIds, port.id) > -1;
        })
    }

    const save = () => {
        try {
            validate();

            AgentApi.Save(agentInputViewData.agentInput)
                .then((response: BaseResponse<AgentModel>) => {
                    if (response.success) {
                        if (agentInputViewData.agentInput.id.length === 0) {
                            Utility.Alert.Success("Agent added successful");
                        } else {
                            Utility.Alert.Success("Agent updated successful");
                        }

                        refreshInput();
                    } else {
                        Utility.Alert.Error(response.message);
                    }
                })
                .catch((error) => {
                    Utility.Alert.Error(error);
                });
        } catch (error) {
            Utility.Alert.Error(error);
        }
    }

    const validate = () => {
        if (agentInputViewData.agentInput.name.length === 0) {
            throw "Agent name is required";
        }

        if (agentInputViewData.agentInput.country.length === 0) {
            throw "Country is required";
        }

        if (!agentInputViewData.agentInput.ports || agentInputViewData.agentInput.ports.length === 0) {
            throw "Port Handling is required";
        }
    }

    const getCountry = () => {
        let country = _.find(contactInputViewData.staticResources.countries, (country) => {
            return country.code === agentInputViewData.agentInput.country
        })

        return country ? country : null;
    }

    return (
        <>
            {
                <CustomAccordion
                    title="Input"
                    defaultOpen={true}
                    className="top-quick-input"
                    open={agentInputViewData.agentInputExpand}
                    onOpenChange={(isOpen: boolean) => {
                        dispatch(setAgentInputExpand(isOpen));
                    }}
                >
                    <Grid container>
                        <Grid item lg={3} xs={12}>
                            <Grid container className='cargo-input-component'>
                                <Grid xs={10} lg={9}>
                                    <TextField
                                        label="Company Name"
                                        fullWidth
                                        value={agentInputViewData.agentInput.name ? agentInputViewData.agentInput.name : ""}
                                        onChange={(event) => {
                                            dispatch(setAgentName(event.target.value));
                                        }}
                                        className='cargo-input-component tonnage-input-full-tc'
                                    />
                                </Grid>
                                <Grid item xs={2} lg={3}>
                                    <Tooltip title="Reset (Alt + R)">
                                        <Button
                                            className="clear-input-button"
                                            tabIndex={-1}
                                            color="success"
                                            variant="contained"
                                            onClick={() => {
                                                refreshInput();
                                            }}
                                        >
                                            <i className="fa fa-refresh" aria-hidden="true"></i>
                                        </Button>
                                    </Tooltip>
                                </Grid>
                            </Grid>
                            <Grid>
                                <Autocomplete
                                    className='cargo-input-component'
                                    options={contactInputViewData.staticResources.countries}
                                    getOptionLabel={(option: CountryModel) => {
                                        return option.name + "(" + option.code + ")"
                                    }}
                                    renderInput={(params) => <TextField {...params} label="Country" />}
                                    value={getCountry()}
                                    onChange={(event: React.SyntheticEvent, value: CountryModel | null, reason: string, details?: AutocompleteChangeDetails<CountryModel> | undefined) => {
                                        dispatch(setCountry(value ? value.code : ""));
                                    }}
                                />
                            </Grid>
                        </Grid>
                        <Grid item lg={2} xs={12}>
                            <PortMultipleSelector
                                onPortChange={(value: PortModel[]) => {
                                    if (value) {
                                        dispatch(setPorts(portsToIds(value)));
                                    }
                                }}
                                useFreeText={true}
                                hideOpposite={true}
                                portLabel='Ports handling'
                                hideArea={true}
                                portValues={getPortsByIds(agentInputViewData.agentInput.ports)}
                                className='cargo-input-component'
                                useAreaBookmark={true}
                            >
                            </PortMultipleSelector>
                        </Grid>
                        <Grid item lg={2} xs={12}>
                            <CargoTypeMultipleSelector
                                onCargoTypeChange={(value: CargoTypeModel[]) => {
                                    if (value) {
                                        dispatch(setCargoesHandling(value));
                                    }
                                }}
                                cargoTypeValues={agentInputViewData.agentInput.cargoesHandling}
                                hideOpposite={true}
                                className="cargo-input-component"
                            >
                            </CargoTypeMultipleSelector>
                        </Grid>
                        <Grid item lg={2} xs={12}>
                            <TextField
                                label="Contact Details"
                                multiline
                                fullWidth
                                rows={3}
                                value={agentInputViewData.agentInput.contactDetails ? agentInputViewData.agentInput.contactDetails : ""}
                                onChange={(event) => {
                                    dispatch(setContactDetails(event.target.value ? event.target.value : ""));
                                }}
                                className='cargo-input-component tonnage-input-full-tc'
                            />
                        </Grid>
                        <Grid item lg={2} xs={12}>
                            <TextField
                                label="Remark"
                                multiline
                                fullWidth
                                rows={3}
                                value={agentInputViewData.agentInput.remark ? agentInputViewData.agentInput.remark : ""}
                                onChange={(event) => {
                                    dispatch(setRemark(event.target.value ? event.target.value : ""));
                                }}
                                className='cargo-input-component tonnage-input-full-tc'
                            />
                        </Grid>
                        <Grid item lg={1} xs={12}>
                            <Tooltip title="Alt + S">
                                <Button
                                    onClick={save}
                                    variant="outlined"
                                >
                                    Save (s)
                                </Button>
                            </Tooltip>
                        </Grid>
                    </Grid>
                </CustomAccordion>
            }
        </>
    )
};

export default AgentInput;